/**
 * useDviEstimate - Hook for managing Phase 3/4 Estimate Drafts
 * 
 * Provides:
 * - Building estimate drafts from finding groups
 * - Phase 4: Review workflow (approve/reject items)
 * - Applying estimate items to ticket
 * - Tracking draft status
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAddonSuggestionTrigger } from '@/contexts/AddonSuggestionContext';
import { useToast } from '@/hooks/use-toast';

export interface EstimateDraft {
  id: string;
  shop_id: string;
  ticket_id: string;
  session_id: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined' | 'archived';
  review_status: 'pending_review' | 'approved' | 'rejected';
  total_labor_minutes: number;
  total_items: number;
  created_at: string;
}

export interface EstimateItem {
  id: string;
  draft_id: string;
  finding_group_id: string;
  shop_id: string;
  ticket_id: string;
  session_id: string;
  title: string | null;
  condition: 'green' | 'yellow' | 'red' | null;
  labor_minutes: number;
  procedure_outline: string | null;
  parts_placeholders: Array<{ name: string; quantity: number }>;
  shop_supplies: string[];
  tools_required: string[];
  notes: string | null;
  review_status: 'pending_review' | 'approved' | 'rejected';
  rejection_reason: string | null;
  added_job_ids: string[];
  added_to_ticket_at: string | null;
  added_to_ticket_by: string | null;
  created_at: string;
}

type IncludeFilter = 'all' | 'yellow_red' | 'red_only';
type ReviewAction = 'approve' | 'reject';

export function useDviEstimate(sessionId: string | null, ticketId: string) {
  // CORE-BP item 6 — see the post-add evaluation in the add-jobs mutation.
  const { evaluateAfterWorkAdded } = useAddonSuggestionTrigger();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch latest draft for this session
  const { data: currentDraft, isLoading: isLoadingDraft } = useQuery({
    queryKey: ['dvi-estimate-draft', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      
      const { data, error } = await supabase
        .from('ticket_dvi_estimate_drafts')
        .select('*')
        .eq('session_id', sessionId)
        .eq('status', 'draft')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as EstimateDraft | null;
    },
    enabled: !!sessionId,
  });

  // Fetch items for current draft
  const { data: draftItems = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ['dvi-estimate-items', currentDraft?.id],
    queryFn: async () => {
      if (!currentDraft?.id) return [];
      
      const { data, error } = await supabase
        .from('ticket_dvi_estimate_items')
        .select('*')
        .eq('draft_id', currentDraft.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        parts_placeholders: (item.parts_placeholders || []) as unknown as Array<{ name: string; quantity: number }>,
        shop_supplies: (item.shop_supplies || []) as unknown as string[],
        tools_required: (item.tools_required || []) as unknown as string[],
      })) as EstimateItem[];
    },
    enabled: !!currentDraft?.id,
  });

  // Build estimate draft mutation
  const buildDraftMutation = useMutation({
    mutationFn: async (include: IncludeFilter = 'yellow_red') => {
      if (!sessionId) throw new Error('No session');
      
      const { data, error } = await supabase.functions.invoke('dvi-build-estimate-draft', {
        body: { session_id: sessionId, include },
      });
      if (error) throw error;
      return data as { success: boolean; draft_id: string; item_count: number; total_labor_minutes: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-estimate-draft', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-estimate-items'] });
      toast({
        title: 'Estimate built',
        description: `${data.item_count} item(s), ${data.total_labor_minutes} minutes total`,
      });
    },
    onError: (error: any) => {
      toast({ title: 'Build failed', description: error.message, variant: 'destructive' });
    },
  });

  // Phase 4: Review items mutation (approve/reject)
  const reviewItemsMutation = useMutation({
    mutationFn: async ({ itemIds, action, rejectionReason }: {
      itemIds: string[];
      action: ReviewAction;
      rejectionReason?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('dvi-review-estimate-items', {
        body: { item_ids: itemIds, action, rejection_reason: rejectionReason },
      });
      if (error) throw error;
      return data as { success: boolean; items_updated: number; draft_status: string };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-estimate-items'] });
      queryClient.invalidateQueries({ queryKey: ['dvi-estimate-draft', sessionId] });
      toast({
        title: variables.action === 'approve' ? 'Items approved' : 'Items rejected',
        description: `${data.items_updated} item(s) updated`,
      });
    },
    onError: (error: any) => {
      toast({ title: 'Review failed', description: error.message, variant: 'destructive' });
    },
  });

  // Apply items to ticket mutation (Phase 4: now requires approval)
  const applyItemsMutation = useMutation({
    mutationFn: async (itemIds?: string[]) => {
      if (!currentDraft?.id) throw new Error('No draft');
      
      // Phase 4: item_ids is now optional - pass empty to apply all approved
      const { data, error } = await supabase.functions.invoke('dvi-apply-estimate-to-ticket', {
        body: { draft_id: currentDraft.id, item_ids: itemIds },
      });
      if (error) throw error;
      return data as { 
        success: boolean; 
        jobs_added: Array<{ job_id: string; job_name: string; minutes: number }>; 
        items_applied: number;
        items_skipped_not_approved: number;
        jobs_skipped_duplicate: number;
        total_minutes: number;
      };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['dvi-estimate-items'] });
      queryClient.invalidateQueries({ queryKey: ['dvi-finding-groups'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      
      // Re-plan the rollover chain (a chain row) or trigger the cascade
      try {
        // CSI-T1a: skip cascade entirely if the ticket was soft-deleted
        // between the dvi-apply-estimate-to-ticket call and this re-fetch.
        // `.maybeSingle()` returns null for the deleted-ticket case;
        // the existing `if (ticketCtx)` guard then short-circuits before
        // any rollover/cascade write.
        const { data: ticketCtx } = await supabase
          .from('tickets')
          .select('shop_id, scheduled_date, assigned_tech_id, parent_ticket_id, sequence_index, rollover_of_ticket_id, split_reason, predicted_minutes')
          .eq('id', ticketId)
          .is('deleted_at', null)
          .maybeSingle();

        if (ticketCtx) {
          // ROLLOVER CHAIN: the server re-planned the chain from its job
          // ledger (dvi-apply-estimate-to-ticket never writes a chain row's
          // minutes); re-asking the authority is idempotent — an already
          // canonical chain writes nothing.
          const { recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
          if (!(await recomputeRolloverChainWork(ticketId))) {
            const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
            // FIX-R35-HIGH-7: don't swallow cascade failures silently. A
            // failed cascade after a DVI estimate apply means the board
            // isn't reflowing around the new predicted minutes — the user
            // needs to see this.
            triggerBoardCascade(
              ticketCtx.shop_id,
              ticketCtx.scheduled_date,
              ticketCtx.assigned_tech_id
            ).catch((err) => {
              console.error('[useDviEstimate] cascade trigger failed:', err, {
                ticketId,
                shopId: ticketCtx.shop_id,
              });
            });
          }
        }
      } catch (err) {
        console.error('[useDviEstimate] Error propagating wall-time after apply:', err);
      }

      const skippedMsg = data.jobs_skipped_duplicate > 0 
        ? `, ${data.jobs_skipped_duplicate} duplicates skipped` 
        : '';
      toast({
        title: 'Jobs added to ticket',
        description: `${data.jobs_added.length} job(s), ${data.total_minutes} minutes${skippedMsg}`,
      });

      // CORE-BP item 6 — DVI-approved work joins an EXISTING ticket, so the
      // same question applies. Asked once, after the batch settled, and only
      // when jobs genuinely landed. This is not DVI-driven suggestion
      // intelligence: the evaluator sees the ticket's work, exactly as it does
      // for a hand-added job, and knows nothing about findings.
      if ((data.jobs_added?.length ?? 0) > 0) {
        await evaluateAfterWorkAdded(ticketId);
      }
    },
    onError: (error: any) => {
      toast({ title: 'Apply failed', description: error.message, variant: 'destructive' });
    },
  });

  // Phase 4: Computed - group items by review status
  const pendingReviewItems = draftItems.filter(item => 
    item.review_status === 'pending_review' && !item.added_to_ticket_at
  );
  const approvedItems = draftItems.filter(item => 
    item.review_status === 'approved' && !item.added_to_ticket_at
  );
  const rejectedItems = draftItems.filter(item => 
    item.review_status === 'rejected' && !item.added_to_ticket_at
  );
  const appliedItems = draftItems.filter(item => !!item.added_to_ticket_at);
  
  // Legacy computed (for backwards compat)
  const unappliedItems = draftItems.filter(item => !item.added_to_ticket_at);
  const totalLaborMinutes = unappliedItems.reduce((sum, item) => sum + item.labor_minutes, 0);

  return {
    currentDraft,
    draftItems,
    
    // Phase 4: Items by review status
    pendingReviewItems,
    approvedItems,
    rejectedItems,
    appliedItems,
    
    // Legacy
    unappliedItems,
    totalLaborMinutes,
    isLoading: isLoadingDraft || isLoadingItems,
    
    // Build mutation
    buildDraft: (include?: IncludeFilter) => buildDraftMutation.mutateAsync(include),
    isBuildingDraft: buildDraftMutation.isPending,
    
    // Phase 4: Review mutations
    approveItems: (itemIds: string[]) => 
      reviewItemsMutation.mutateAsync({ itemIds, action: 'approve' }),
    rejectItems: (itemIds: string[], reason?: string) => 
      reviewItemsMutation.mutateAsync({ itemIds, action: 'reject', rejectionReason: reason }),
    isReviewing: reviewItemsMutation.isPending,
    
    // Apply mutations (Phase 4: now only applies approved items)
    applyItems: (itemIds: string[]) => applyItemsMutation.mutateAsync(itemIds),
    applyAllApproved: () => applyItemsMutation.mutateAsync(undefined), // Apply all approved
    isApplying: applyItemsMutation.isPending,
  };
}
